import React, { useEffect, useState } from "react";
import api, { formatErr, money } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Broom, ArrowClockwise } from "@phosphor-icons/react";

export default function DuplicateItemsDialog({ open, onOpenChange, onCleaned }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ groups: [], group_count: 0, duplicate_rows: 0 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/items/duplicates");
      setData(r.data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const runDedupe = async () => {
    const msg =
      `Merge ${data.duplicate_rows} duplicate row(s) across ${data.group_count} item code(s)?\n\n` +
      `• Keeper = highest stock (latest-updated if tie)\n` +
      `• Stock will be SUMMED into keeper\n` +
      `• Prices updated from most recently edited duplicate\n` +
      `• Other duplicate rows will be DELETED\n\n` +
      `Old invoices / purchases keep their snapshot. This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const r = await api.post("/items/dedupe");
      toast.success(`Merged ${r.data.merged_groups} groups • Deleted ${r.data.deleted_items} rows`);
      await load();
      if (onCleaned) onCleaned();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="duplicates-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Broom size={22} weight="duotone" /> Remove Duplicate Items
          </DialogTitle>
          <div className="text-xs text-muted-foreground">
            Same item code (case-insensitive) waale items ek me merge honge. Stock sum, prices latest se.
          </div>
        </DialogHeader>

        <div className="flex items-center gap-3 py-2 border-y border-border">
          <div className="text-sm">
            <span className="font-semibold text-lg tabular-nums">{data.group_count}</span>{" "}
            <span className="text-muted-foreground">duplicate group(s)</span>
            <span className="mx-2 text-muted-foreground">•</span>
            <span className="font-semibold text-lg tabular-nums text-destructive">{data.duplicate_rows}</span>{" "}
            <span className="text-muted-foreground">extra row(s) will be removed</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="duplicates-refresh">
              <ArrowClockwise size={14} className="mr-1" /> Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="py-10 text-center text-muted-foreground text-sm">Scanning items…</div>
        ) : data.groups.length === 0 ? (
          <div className="py-10 text-center text-sm">
            <div className="text-4xl mb-2">✨</div>
            <div className="font-semibold">No duplicates found</div>
            <div className="text-muted-foreground text-xs mt-1">Your item master is clean.</div>
          </div>
        ) : (
          <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-1">
            {data.groups.map((g) => (
              <div key={g.code} className="border border-border rounded" data-testid={`duplicate-group-${g.code}`}>
                <div className="px-3 py-2 bg-amber-50/60 border-b border-border flex items-center justify-between">
                  <div>
                    <span className="font-mono font-semibold text-sm">{g.code}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      {g.count} entries → 1 keeper + {g.count - 1} to delete
                    </span>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24">Code</TableHead>
                      <TableHead>Item Name</TableHead>
                      <TableHead>Brand</TableHead>
                      <TableHead className="text-right">Stock</TableHead>
                      <TableHead className="text-right">Purchase</TableHead>
                      <TableHead className="text-right">Sale</TableHead>
                      <TableHead className="text-xs">Updated</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {g.items.map((it) => (
                      <TableRow key={it.id}>
                        <TableCell className="font-mono text-xs">{it.item_code}</TableCell>
                        <TableCell className="text-sm">{it.item_name}</TableCell>
                        <TableCell className="text-xs">{it.brand || "-"}</TableCell>
                        <TableCell className="text-right tabular-nums">{it.current_stock}</TableCell>
                        <TableCell className="text-right tabular-nums">{money(it.purchase_price)}</TableCell>
                        <TableCell className="text-right tabular-nums">{money(it.sale_price)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {(it.updated_at || "").slice(0, 10) || "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="duplicates-close">
            Close
          </Button>
          <Button
            onClick={runDedupe}
            disabled={busy || data.groups.length === 0}
            className="bg-destructive hover:bg-destructive/90 text-white"
            data-testid="duplicates-merge"
          >
            {busy ? "Merging…" : `Merge & Delete ${data.duplicate_rows} row(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
